import React, { useContext, useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import moment from "moment";
import {
  listCredentials,
  deleteCredential,
} from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import { SCOPE_BY_VALUE } from "../scopesCatalogue";
import CreateCredentialModal from "./createCredentialModal";

const PAGE_LIMIT = 20;

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm") : "—");

const ScopeBadges = ({ scopes }) => {
  if (!scopes || scopes.length === 0) return <span className="text-muted">—</span>;
  const head = scopes.slice(0, 2);
  const rest = scopes.length - head.length;
  const tooltip = scopes
    .map((s) => (SCOPE_BY_VALUE[s] ? `${SCOPE_BY_VALUE[s].label} (${s})` : s))
    .join("\n");
  return (
    <span title={tooltip}>
      {head.map((s) => (
        <span key={s} className="badge bg-secondary me-1">
          {s}
        </span>
      ))}
      {rest > 0 && (
        <span className="badge bg-light text-dark">+{rest} more</span>
      )}
    </span>
  );
};

const DeviceCell = ({ ids }) => {
  if (!ids || ids.length === 0) return <span className="text-muted">All devices</span>;
  return (
    <span title={`Device IDs: ${ids.join(", ")}`}>
      {ids.length} device{ids.length === 1 ? "" : "s"}
    </span>
  );
};

const CredentialsTab = ({ appPubID }) => {
  const { showSuccess, showError } = useContext(ToastrContext);
  const [creds, setCreds] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = async (after = null) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      if (after) params.starting_after = after;
      const res = await listCredentials(appPubID, params);
      const items = Array.isArray(res.data) ? res.data : [];
      setCreds((prev) => (after ? [...prev, ...items] : items));
      setHasMore(!!res.has_more);
      setCursor(res.next_cursor || null);
    } catch (e) {
      showError((e && e.message) || "Failed to load credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCreds([]);
    setCursor(null);
    setHasMore(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPubID]);

  const handleCreated = () => {
    setCreds([]);
    setCursor(null);
    setHasMore(false);
    load();
  };

  const confirmAndRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await deleteCredential(appPubID, confirmRevoke.id);
      showSuccess("Credential revoked");
      setConfirmRevoke(null);
      handleCreated();
    } catch (e) {
      showError((e && e.message) || "Failed to revoke credential");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">API Credentials</h5>
          <p className="text-muted mb-0 small">
            Bearer tokens used by external integrations. Secrets are shown
            once at creation and can never be retrieved again.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          <FiPlus className="align-middle me-1" size={16} />
          New Credential
        </Button>
      </div>

      <div className="table-responsive">
        {loading && creds.length === 0 ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : creds.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <p className="mb-0">No credentials yet.</p>
          </div>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Key ID</th>
                <th>Environment</th>
                <th>Scopes</th>
                <th>Device Allowlist</th>
                <th>Last Used</th>
                <th>Expires</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creds.map((c) => {
                const revoked = !!c.ts_revoked;
                return (
                  <tr key={c.id}>
                    <td>
                      <code>{c.key_id}</code>
                    </td>
                    <td>
                      <span
                        className={`badge bg-${
                          c.environment === "live" ? "primary" : "info"
                        }`}
                      >
                        {c.environment}
                      </span>
                    </td>
                    <td>
                      <ScopeBadges scopes={c.scopes} />
                    </td>
                    <td>
                      <DeviceCell ids={c.device_ids} />
                    </td>
                    <td>{formatTs(c.ts_last_used)}</td>
                    <td>{formatTs(c.ts_expires)}</td>
                    <td>{formatTs(c.ts_created)}</td>
                    <td>
                      {revoked ? (
                        <span className="badge bg-danger">Revoked</span>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setConfirmRevoke(c)}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {hasMore && (
          <div className="text-center">
            <Button
              variant="outline-secondary"
              onClick={() => load(cursor)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <CreateCredentialModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        appPubID={appPubID}
        onCreated={handleCreated}
      />

      <Modal
        show={!!confirmRevoke}
        onHide={() => !revoking && setConfirmRevoke(null)}
        centered
        size="sm"
        backdrop="static"
      >
        <Modal.Header className="bg-danger-subtle" closeButton={!revoking}>
          <Modal.Title>Revoke credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will permanently revoke the credential below. Any integration
            using its secret will immediately stop working.
          </p>
          {confirmRevoke && (
            <p className="mb-0">
              <code>{confirmRevoke.key_id}</code>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmRevoke(null)}
            disabled={revoking}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmAndRevoke}
            disabled={revoking}
          >
            {revoking ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Revoking...
              </>
            ) : (
              "Revoke"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CredentialsTab;
